import React, { useState, useEffect } from 'react'
import { Button, Dialog, DialogContent, DialogActions, Typography, Box, Stack, IconButton } from '@mui/material'
import { Close, GppMaybe, ArrowForward } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { getMFAStatus } from 'service/mfa'
import { accent } from 'mainTheme'

const SESSION_KEY = 'mfa_prompt_dismissed'

const MFAPromptDialog = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(false)

  const { data: mfaEnabled } = useQuery({
    queryKey: ['mfaStatus'],
    queryFn: getMFAStatus,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    select: (data) => data.totpEnabled
  })

  useEffect(() => {
    if (mfaEnabled === false && !sessionStorage.getItem(SESSION_KEY)) {
      // Small delay so the app fully renders before the prompt appears
      const timer = setTimeout(() => setOpen(true), 800)
      return () => clearTimeout(timer)
    }
  }, [mfaEnabled])

  const dismiss = (withSnack) => {
    sessionStorage.setItem(SESSION_KEY, '1')
    setOpen(false)
    if (withSnack) {
      enqueueSnackbar('You can enable two-factor authentication anytime from your Account page.', {
        variant: 'info'
      })
    }
  }

  const handleEnable = () => {
    dismiss(false)
    navigate('/admin/accounts', { state: { openMFA: true } })
  }

  const handleLater = () => dismiss(true)

  return (
    <Dialog
      open={open}
      onClose={handleLater}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.08)',
          bgcolor: 'background.paper',
          overflow: 'visible'
        }
      }}>
      {/* Close button */}
      <IconButton
        size="small"
        onClick={handleLater}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'rgba(255,255,255,0.3)',
          zIndex: 1,
          '&:hover': { color: 'rgba(255,255,255,0.6)' }
        }}>
        <Close color="secondary" fontSize="small" />
      </IconButton>

      <DialogContent sx={{ pt: 4, pb: 2, px: 3 }}>
        <Stack alignItems="center" spacing={2.5}>
          {/* Icon badge */}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: `${accent}18`,
              border: `1.5px solid ${accent}55`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 32px ${accent}22`
            }}>
            <GppMaybe sx={{ fontSize: 36, color: accent }} />
          </Box>

          {/* Text */}
          <Box textAlign="center">
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, fontSize: '17px', letterSpacing: '-0.01em' }}>
              Secure Your Account
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.6, lineHeight: 1.65, fontSize: '13.5px', maxWidth: 280, mx: 'auto' }}>
              Add an extra layer of protection with two-factor authentication. You&apos;ll need your authenticator app
              each time you sign in.
            </Typography>
          </Box>

          {/* Feature list */}
          <Stack
            spacing={1}
            sx={{
              width: '100%',
              bgcolor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 2,
              p: 2
            }}>
            {[
              'Protect against unauthorised access',
              'Works with Google & Microsoft Authenticator',
              'Takes less than 2 minutes to set up'
            ].map((text) => (
              <Stack key={text} direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: accent,
                    flexShrink: 0
                  }}
                />
                <Typography variant="body2" sx={{ opacity: 0.65, fontSize: '12.5px' }}>
                  {text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={handleLater} sx={{ flex: 1, color: 'rgba(255,255,255,0.45)', fontSize: '13px' }}>
          Maybe Later
        </Button>
        <Button
          onClick={handleEnable}
          variant="contained"
          endIcon={<ArrowForward sx={{ fontSize: '16px !important' }} />}
          sx={{ flex: 2, fontSize: '13px' }}>
          Enable Now
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MFAPromptDialog
